// src/components/HeroCarousel.jsx
import { useEffect, useRef, useState } from "react";
import "./HeroCarousel.css";
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'aos/dist/aos.css';

const hero1 = "/hero1.jpg";
const hero2 = "/hero2.jpg";
const hero3 = "/hero3.jpg";

const defaultSlides = [
  { id: 1, title: ["Wedding Wonders", "@Varaha!"], subtitle: ["Pure Kanchipuram", "& Banarasi Silks"], img: hero1, cta: "Shop Sarees", href: "/collections", focus: "center center" },
  { id: 2, title: ["Handloom", "Heritage"], subtitle: ["Authentic weaves,", "artisan-made"], img: hero2, cta: "Explore Collection", href: "/collections", focus: "center right" },
  { id: 3, title: ["Festive Silk", "Edit"], subtitle: ["Paithani • Gadwal •", "Uppada • Mysore"], img: hero3, cta: "Browse Now", href: "/collections", focus: "center center" },
];

export default function HeroCarousel({ banners = [] }) {
  const slides = banners.length > 0
    ? banners.map((banner, index) => ({
      id: banner.id || index + 1,
      title: banner.title
        ? String(banner.title).split(' ').slice(0, 2)
        : ["Varaha", "Silks"],
      subtitle: banner.subtitle
        ? String(banner.subtitle).split(' ').slice(0, 2)
        : ["Premium Sarees", "Collection"],
      img: banner.imageUrl || banner.image || banner.bannerImage || hero1,
      cta: banner.ctaText || banner.cta || "Shop Now",
      href: banner.ctaLink || banner.linkUrl || "/collections",
      focus: banner.focus || "center center"
    }))
    : defaultSlides;

  // All hooks must be called at the top level, before any conditional returns
  const sectionRef = useRef(null);
  const [imgY, setImgY] = useState(0);
  const [txtY, setTxtY] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set([0])); // Preload first image
  const [firstImageLoaded, setFirstImageLoaded] = useState(false); // Track first image load
  const [carouselActive, setCarouselActive] = useState(false); // Control carousel initialization

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    let raf = 0;
    const imgRange = 60, txtRange = 28;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 0;
        const t = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
        const centered = (t - 0.5) * 2;
        setImgY(centered * imgRange);
        setTxtY(centered * -txtRange);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Only start carousel auto-advance AFTER first image is loaded
  useEffect(() => {
    if (!firstImageLoaded || !carouselActive) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length, firstImageLoaded, carouselActive]);

  // Preload next image when slide changes (optimize loading)
  useEffect(() => {
    if (!carouselActive) return;

    const nextSlide = (currentSlide + 1) % slides.length;
    if (!loadedImages.has(nextSlide)) {
      // Preload next image in background
      const img = new Image();
      img.src = slides[nextSlide].img;
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, nextSlide]));
      };
    }
  }, [currentSlide, loadedImages, slides, carouselActive]);

  // Handler for first image load - activates carousel
  const handleFirstImageLoad = () => {
    setFirstImageLoaded(true);
    setCarouselActive(true);
  };

  return (
    <section ref={sectionRef} className="hero-carousel" data-aos="fade-up">
      <div className="hero-carousel-fallback">
        {slides.map((s, index) => {
          const isActive = index === currentSlide;
          const isNext = index === (currentSlide + 1) % slides.length;
          const shouldLoad = isActive || isNext || loadedImages.has(index);

          return (
            <div
              key={s.id}
              className={`slide ${isActive ? 'active' : ''}`}
              style={{
                display: isActive ? 'block' : 'none',
                position: 'relative',
                height: '100vh',
                overflow: 'hidden'
              }}
            >
              {shouldLoad ? (
                <img
                  className="slide-img"
                  src={s.img}
                  alt={`${s.title[0]} ${s.title[1]}`}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchpriority={index === 0 ? "high" : "low"}
                  width="1920"
                  height="1080"
                  onLoad={index === 0 ? handleFirstImageLoad : undefined}
                  style={{
                    objectPosition: s.focus,
                    transform: `translate3d(0, ${imgY}px, 0)`,
                    willChange: "transform",
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    imageRendering: 'auto'
                  }}
                />
              ) : (
                // Placeholder to maintain layout
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f0f0f0'
                  }}
                />
              )}
              <div className="overlay">
                <div className="overlay-inner" style={{ transform: `translate3d(0, ${txtY}px, 0)`, willChange: "transform" }}>
                  <h2 className="weave-hero-title duo-title">
                    <span className="tone1">{s.title[0]}</span> <span className="tone2">{s.title[1]}</span>
                  </h2>
                  <p className="weave-hero-sub duo-subtitle">
                    <span className="tone1">{s.subtitle[0]}</span> <span className="tone2">{s.subtitle[1]}</span>
                  </p>
                  <a className="btn-ghost" href={s.href}>{s.cta}</a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
